import React, { useRef, useState, useEffect } from 'react';
import { View, FlatList, Dimensions } from 'react-native';

import { ViewPropTypes, TextPropTypes } from 'deprecated-react-native-prop-types';
import PropTypes from 'prop-types';

import FoundationTestKeys from '../../FoundationTestKeys';
import { LightThemeColors } from '../Themes';
import VFText from '../VFText';

import DefaultItem from './DefaultItem';
import styles from './SelectionList.Styles';

const { width } = Dimensions.get('window');

const renderItem = ({
  theme,
  option,
  index,
  onSelectOption,
  setSelectedIndex,
  itemContainerStyle,
  selectedIndex,
  customItem,
  activeSubtitleStyle,
  activeTitleStyle,
  activeContainerStyle,
  inActiveTitleStyle,
  inActiveSubtitleStyle,
}) => {
  const isActive = index === selectedIndex;
  const onPressItem = () => {
    if (!isActive) {
      setSelectedIndex(index);
      onSelectOption(index, option, isActive);
    }
  };

  if (customItem) {
    return React.cloneElement(customItem, {
      theme,
      index,
      isActive,
      onPress: onPressItem,
      option,
    });
  }

  return (
    <DefaultItem
      titleTextStyle={[
        styles.titleTextStyle(theme, isActive),
        isActive ? activeTitleStyle : inActiveTitleStyle,
      ]}
      subTitleTextStyle={[
        styles.subTitleTextStyle(theme),
        isActive ? activeSubtitleStyle : inActiveSubtitleStyle,
      ]}
      isActive={isActive}
      onPress={onPressItem}
      containerStyle={[itemContainerStyle, isActive ? activeContainerStyle : undefined]}
      {...option}
    />
  );
};

const SelectionList = ({
  containerStyle,
  title,
  description,
  listStyle,
  options,
  theme,
  onSelectOption,
  withInitialScrollToIndex,
  scrollToIndexViewOffset,
  itemContainerStyle,
  initialSelectedIndex,
  customItem,
  itemWidth,
  activeSubtitleStyle,
  activeTitleStyle,
  activeContainerStyle,
  inActiveSubtitleStyle,
  inActiveTitleStyle,
  descriptionStyle,
  ...props
}) => {
  const listRef = useRef();
  const [isScrolledFirstTime, setIsScrolledFirstTime] = useState(false);

  const _initSelectedIndex =
    !initialSelectedIndex || (initialSelectedIndex < options?.length && initialSelectedIndex >= 0)
      ? initialSelectedIndex
      : 0;

  const [selectedIndex, setSelectedIndex] = useState(_initSelectedIndex);

  useEffect(() => {
    if (withInitialScrollToIndex && itemWidth && listRef.current && isScrolledFirstTime) {
      const indexToAllowAutoScrollAfter = width / itemWidth / 2;
      listRef.current.scrollToIndex({
        animated: !!_initSelectedIndex,
        index: _initSelectedIndex || 0,
        viewPosition: 0.5,
        viewOffset:
          selectedIndex > indexToAllowAutoScrollAfter && scrollToIndexViewOffset
            ? scrollToIndexViewOffset
            : 0,
      });
    } else if (!isScrolledFirstTime) {
      setIsScrolledFirstTime(true);
    }
  }, [selectedIndex]);

  return (
    <View style={containerStyle} testID={FoundationTestKeys.selectionListView}>
      {title && (
        <VFText
          style={styles.title}
          i18nKey={title}
          testKey={FoundationTestKeys.selectionListTitle}
        />
      )}
      <FlatList
        initialScrollIndex={initialSelectedIndex}
        {...props}
        style={listStyle}
        ref={listRef}
        horizontal
        showsHorizontalScrollIndicator={false}
        bounces={false}
        data={options}
        getItemLayout={
          itemWidth
            ? (data, index) => ({
                length: itemWidth,
                index,
                offset: itemWidth * index,
              })
            : null
        }
        keyExtractor={(item, index) => `${index}`}
        renderItem={({ item, index }) =>
          renderItem({
            option: item,
            index,
            theme,
            onSelectOption,
            setSelectedIndex,
            itemContainerStyle,
            selectedIndex,
            customItem,
            activeSubtitleStyle,
            activeTitleStyle,
            activeContainerStyle,
            inActiveSubtitleStyle,
            inActiveTitleStyle,
          })
        }
        testID={FoundationTestKeys.selectionListFlatList}
      />
      {description && (
        <VFText
          style={[styles.description, descriptionStyle]}
          i18nKey={description}
          testKey={FoundationTestKeys.selectionListDescription}
        />
      )}
    </View>
  );
};

SelectionList.defaultProps = {
  options: [],
  isActive: false,
  selectOption: () => {},
  theme: LightThemeColors,
};

SelectionList.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  listStyle: ViewPropTypes.style,
  containerStyle: ViewPropTypes.style,
  activeSubtitleStyle: TextPropTypes.style,
  activeTitleStyle: TextPropTypes.style,
  inActiveSubtitleStyle: TextPropTypes.style,
  inActiveTitleStyle: TextPropTypes.style,
  descriptionStyle: ViewPropTypes.style,
  activeContainerStyle: ViewPropTypes.style,
  options: PropTypes.array,
  onSelectOption: PropTypes.func,
  withInitialScrollToIndex: PropTypes.bool,
  scrollToIndexViewOffset: PropTypes.number,
  customItem: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  initialSelectedIndex: PropTypes.number,
  itemWidth: PropTypes.number,
  selectOption: PropTypes.func,
  itemContainerStyle: ViewPropTypes.style,
  contentContainerStyle: ViewPropTypes.style,
  theme: PropTypes.shape({
    name: PropTypes.string,
    isDark: PropTypes.bool,
    colors: PropTypes.object,
  }),
};
export default SelectionList;
